import React, { useState, useEffect, useRef, useContext } from 'react';
import styles from './KYC.module.css';
import { BaseUrlContext } from '../../context/BaseUrlContext';
import axios from 'axios';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import CircularProgress from '@mui/material/CircularProgress';
import PlacesAutocomplete, {
  geocodeByAddress,
  geocodeByPlaceId,
  getLatLng,
} from 'react-places-autocomplete';
import toast from 'react-hot-toast';

export default function NewKYC() {
  const [currentStep, setCurrentStep] = useState(1);
  const [welcomeTextIndex, setWelcomeTextIndex] = useState(0);
  const [animationState, setAnimationState] = useState('active');
  
  // Business Info Form States (matching RegisterPage)
  const [category, setCategory] = useState('');
  const [email, setEmail] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [storePhone, setStorePhone] = useState('');
  const [onBoardedBy, setOnBoardedBy] = useState('self');
  const [onBoardedByName, setOnBoardedByName] = useState('');
  
  // Location states (matching RegisterPage)
  const [location, setLocation] = useState({ latitude: '', longitude: '' });
  const [store_address, setAddr] = useState('');
  const [currentLocationCheck, setCurrentLocationCheck] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addressResults, setAddressResults] = useState([]);
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [states, setStates] = useState([]);
  const [bankCode, setBankCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [banks, setBanks] = useState([]);
  const [isResolvingAccount, setIsResolvingAccount] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [productCategories, setProductCategories] = useState([]);
  const [selectedProductCategories, setSelectedProductCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm_password, setCPassword] = useState('');
  const [agree, setAgree] = useState(false);
  const [doesNotMath, setDoesNotMath] = useState(false);
  const [showText, setShowText] = useState(false);
  const [showText1, setShowText1] = useState(false);
  
  // Get base URL from context (matching RegisterPage)
  const baseUrl = useContext(BaseUrlContext);
  
  // Wallet Form States
  const [formData, setFormData] = useState({
    businessType: 'Sole Proprietorship',
    bvn: '',
    dateOfBirth: '',
    idType: 'NIN Slip',
    idNumber: '',
    cacRegistrationNumber: '',
    tin: ''
  });

  // Document upload states
  const [cacDocument, setCacDocument] = useState(null);
  const [idDocument, setIdDocument] = useState(null);
  const [utilityBill, setUtilityBill] = useState(null);
  const [faceVerified, setFaceVerified] = useState(false);

  const animationTimeoutRef = useRef(null);
  const cycleTimeoutRef = useRef(null);

  const welcomeMessages = [
    {
      title: "Welcome, Let's get to know",
      subtitle: "more about your business"
    },
    {
      title: "Ready to grow your business?",
      subtitle: "Let's set up your account"
    },
    {
      title: "Join thousands of businesses",
      subtitle: "already using our platform"
    },
    {
      title: "Secure, fast, and reliable",
      subtitle: "business solutions await you"
    },
    {
      title: "Your success journey starts",
      subtitle: "with this simple setup"
    }
  ];

  // Simple, working animation system
  useEffect(() => {
    const interval = setInterval(() => {
      // Force a re-render by changing the key
      setWelcomeTextIndex((prevIndex) => 
        (prevIndex + 1) % welcomeMessages.length
      );
    }, 6000); // Increased from 4000ms to 6000ms (6 seconds)

    return () => clearInterval(interval);
  }, [welcomeMessages.length]);

  // Fetch banks on component mount (matching RegisterPage)
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const banksApi = await fetch('https://api.paystack.co/bank');
        const banksJson = await banksApi.json();
        if (banksJson.status) {
          setBanks(banksJson.data);
        }
      } catch(e) {
        console.error("Could not fetch banks", e);
      }
    };
    fetchBanks();
  }, []);

  // Fetch product categories when category is 'brand' (matching RegisterPage)
  useEffect(() => {
    const fetchProductCategories = async () => {
      if (category === 'brand') {
        setIsLoadingCategories(true);
        try {
          const url = `${baseUrl}/product-category/all`;
          console.log('Fetching product categories from:', url);
          
          // Use the public endpoint for product categories (matching RegisterPage)
          const response = await axios.get(url);
          console.log('Product categories response:', response.data);
          
          if (response.data && response.data.success && response.data.data) {
            // Public endpoint returns: { success: true, code: 200, data: [...] }
            const categories = response.data.data || [];
            console.log('Setting product categories:', categories);
            
            if (categories.length > 0) {
              setProductCategories(categories);
            } else {
              console.log('API returned empty categories, using fallback categories');
              // Fallback categories if API returns empty data
              const fallbackCategories = [
                { _id: 'beverages', category_name: 'Beverages' },
                { _id: 'table-water', category_name: 'Table water' },
                { _id: 'snacks', category_name: 'Snacks' },
                { _id: 'home-appliances', category_name: 'Home appliances' },
                { _id: 'games', category_name: 'Games' },
                { _id: 'beauty', category_name: 'Beauty' },
                { _id: 'perfumes', category_name: 'Perfumes' },
                { _id: 'electronics', category_name: 'Electronics' },
                { _id: 'clothing', category_name: 'Clothing & Fashion' },
                { _id: 'food', category_name: 'Food & Groceries' },
              ];
              setProductCategories(fallbackCategories);
            }
          } else {
            console.log('API response format unexpected, using fallback categories');
            const fallbackCategories = [
              { _id: 'beverages', category_name: 'Beverages' },
              { _id: 'table-water', category_name: 'Table water' },
              { _id: 'snacks', category_name: 'Snacks' },
              { _id: 'home-appliances', category_name: 'Home appliances' },
              { _id: 'games', category_name: 'Games' },
              { _id: 'beauty', category_name: 'Beauty' },
              { _id: 'perfumes', category_name: 'Perfumes' },
              { _id: 'electronics', category_name: 'Electronics' },
              { _id: 'clothing', category_name: 'Clothing & Fashion' },
              { _id: 'food', category_name: 'Food & Groceries' },
            ];
            setProductCategories(fallbackCategories);
          }
        } catch(e) {
          console.error("Could not fetch product categories", e);
          // Use fallback categories on error
          const fallbackCategories = [
            { _id: 'beverages', category_name: 'Beverages' },
            { _id: 'table-water', category_name: 'Table water' },
            { _id: 'snacks', category_name: 'Snacks' },
            { _id: 'home-appliances', category_name: 'Home appliances' },
            { _id: 'games', category_name: 'Games' },
            { _id: 'beauty', category_name: 'Beauty' },
            { _id: 'perfumes', category_name: 'Perfumes' },
            { _id: 'electronics', category_name: 'Electronics' },
            { _id: 'clothing', category_name: 'Clothing & Fashion' },
            { _id: 'food', category_name: 'Food & Groceries' },
          ];
          setProductCategories(fallbackCategories);
        } finally {
          setIsLoadingCategories(false);
        }
      }
    };
    fetchProductCategories();
  }, [category, baseUrl]);

  const businessTypes = [
    'Sole Proprietorship',
    'Partnership',
    'Limited Liability Company (LLC)',
    'Corporation',
    'Cooperative',
    'Non-Profit Organization'
  ];

  const idTypes = [
    'NIN Slip',
    'Driver\'s License',
    'International Passport',
    'Voter\'s Card',
    'National ID Card'
  ];

  // Hardcoded countries (matching RegisterPage)
  const hardcodedCountries = [
    { name: 'Nigeria', states: ['Lagos', 'Abuja', 'Kano', 'Rivers'] },
    { name: 'Kenya', states: ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'] },
    { name: 'Egypt', states: ['Cairo', 'Giza', 'Alexandria', 'Aswan'] },
    { name: 'Brazil', states: ['São Paulo', 'Rio de Janeiro', 'Bahia', 'Paraná'] },
    { name: 'Argentina', states: ['Buenos Aires', 'Córdoba', 'Mendoza', 'Santa Fe'] },
    { name: 'Colombia', states: ['Bogotá', 'Medellín', 'Cali', 'Barranquilla'] },
    { name: 'United Kingdom', states: ['England', 'Scotland', 'Wales', 'Northern Ireland'] },
    { name: 'Germany', states: ['Bavaria', 'Berlin', 'Hamburg', 'Hesse'] },
    { name: 'France', states: ['Île-de-France', 'Provence', 'Normandy', 'Brittany'] },
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Business Info Form Handlers (matching RegisterPage)
  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    // Reset product categories when category changes
    if (e.target.value !== 'brand') {
      setSelectedProductCategories([]);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleStoreNameChange = (e) => {
    setStoreName(e.target.value);
  };

  const handleStoreAddressChange = (value) => {
    // Handle both PlacesAutocomplete (string) and regular input (event)
    if (typeof value === 'string') {
      setStoreAddress(value);
      setAddr(value);
    } else if (value && value.target) {
      setStoreAddress(value.target.value);
      setAddr(value.target.value);
    }
  };

  const handleStorePhoneChange = (e) => {
    setStorePhone(e.target.value);
  };

  const handleOnboardedChange = (e) => {
    setOnBoardedBy(e.target.value);
  };

  const handleOnboardedName = (e) => {
    setOnBoardedByName(e.target.value);
  };

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setCountry(selectedCountry);
    setState(''); // Reset state when country changes
    
    // Find states for selected country
    const countryData = hardcodedCountries.find(c => c.name === selectedCountry);
    if (countryData) {
      setStates(countryData.states);
    } else {
      setStates([]);
    }
  };

  const handleStateChange = (e) => {
    setState(e.target.value);
  };

  const handleBankChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedBank(selectedOption);
      setBankCode(selectedOption.value);
      setBankName(selectedOption.label);
      setAccountNumber('');
      setAccountName('');
    } else {
      setSelectedBank(null);
      setBankCode('');
      setBankName('');
      setAccountNumber('');
      setAccountName('');
    }
  };

  const handleAccountNumberChange = (e) => {
    setAccountNumber(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleCPasswordChange = (e) => {
    setCPassword(e.target.value);
    setDoesNotMath(e.target.value !== password);
  };

  const handleAgreeChange = (e) => {
    setAgree(e.target.checked);
  };

  const changeType = () => {
    setShowText(!showText);
  };

  const changeType1 = () => {
    setShowText1(!showText1);
  };

  const handleProductCategoryChange = (selectedOptions) => {
    setSelectedProductCategories(selectedOptions || []);
  };

  // Location handlers (matching RegisterPage)
  const handleUseLiveLocation = () => {
    setIsLoading(true);
    setCurrentLocationCheck(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });

          if (latitude !== null && longitude !== null) {
            fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${latitude},+${longitude}&key=AIzaSyCvH_nDKUVAcmjtKEbv-_YDY9rVbGqg4VQ`
            )
              .then((response) => response.json())
              .then((data) => {
                if (data.results && data.results.length > 0) {
                  const uniqueAddresses = [];
                  data.results.forEach((result) => {
                    if (!uniqueAddresses.includes(result.formatted_address)) {
                      uniqueAddresses.push(result.formatted_address);
                    }
                  });
                  setAddressResults(uniqueAddresses);
                  if (uniqueAddresses.length > 0) {
                    setAddr(uniqueAddresses[0]);
                    setStoreAddress(uniqueAddresses[0])
                  }
                } else {
                  toast.error('Could not find any addresses for your location.');
                  setCurrentLocationCheck(false);
                }
              })
              .catch((error) => {
                toast.error('Failed to fetch address from Google Maps.');
                setCurrentLocationCheck(false);
              })
              .finally(() => {
                setIsLoading(false);
              });
          } else {
            toast.error('Could not retrieve your coordinates.');
            setIsLoading(false);
            setCurrentLocationCheck(false);
          }
        },
        (error) => {
          toast.error(`Geolocation error: ${error.message}`);
          setIsLoading(false);
          setCurrentLocationCheck(false);
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser.');
      setIsLoading(false);
      setCurrentLocationCheck(false);
    }
  };

  const handleManualLocation = () => {
    setCurrentLocationCheck(false);
  }

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const { lat, lng } = await getLatLng(results[0]);
    setLocation({
      latitude: lat,
      longitude: lng,
    });
    setAddr(value);
  };

  const handleAddressChange = (e) => {
    setAddr(e.target.value);
  };

  const handleSkip = () => {
    // Mark KYC as completed when skipping
    localStorage.setItem('kyc_completed', 'true');
    
    // Navigate to dashboard - this should work from any step
    window.location.href = '/app/dashboard';
  };

  const handleNext = () => {
    console.log('Current step:', currentStep);
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 3) {
      // Complete KYC and redirect to dashboard
      console.log('Completing KYC...');
      handleKYCCompletion();
    }
  };

  const handleKYCCompletion = () => {
    // Since there's no backend, just redirect to dashboard
    // In production, you would save KYC data here
    
    // Mark KYC as completed in localStorage
    localStorage.setItem('kyc_completed', 'true');
    
    // Redirect to dashboard
    window.location.href = '/app/dashboard';
  };

  const isKYCComplete = () => {
    // Simplified validation - just check basic fields
    // In production, you would have more comprehensive validation
    return true; // Always return true for now
  };

  const handleFileUpload = (file, type) => {
    if (type === 'cac') {
      setCacDocument(file);
    } else if (type === 'id') {
      setIdDocument(file);
    } else if (type === 'utility') {
      setUtilityBill(file);
    }
  };

  const handleClearUpload = (type) => {
    if (type === 'cac') {
      setCacDocument(null);
    } else if (type === 'id') {
      setIdDocument(null);
    } else if (type === 'utility') {
      setUtilityBill(null);
    }
  };

  const handleFaceVerification = () => {
    setFaceVerified(true);
    // Here you would integrate with actual face verification service
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <h2>Business Information</h2>
              <p>Please enter the relevant details.</p>
            </div>
            <div className={styles.form}>
              {/* Business Category */}
              <div className={styles.formGroup}>
                <label className={styles.required}>Business Category</label>
                <select
                  className={styles.selectField}
                  value={category}
                  onChange={handleCategoryChange}
                  required
                >
                  <option value="" disabled>Select Business Category</option>
                  <option value="brand">Manufacturer</option>
                  <option value="kiosk">Kiosk</option>
                  <option value="mini_mart">Ministore</option>
                  <option value="supermarket">Supermarket</option>
                </select>
                  </div>

              {/* Onboarded by */}
              <div className={styles.formGroup}>
                <label className={styles.required}>Onboarded by*</label>
                <select
                  className={styles.selectField}
                  value={onBoardedBy}
                  onChange={handleOnboardedChange}
                >
                  <option value="self">Myself</option>
                  <option value="agent">Agent</option>
                </select>
              </div>
              
              {/* Agent code/Name - only show when onboarded by agent */}
              {onBoardedBy === 'agent' && (
              <div className={styles.formGroup}>
                  <label className={styles.required}>Agent code/Name</label>
                <input
                  type="text"
                  className={styles.inputField}
                    placeholder="Enter agent code or name"
                    value={onBoardedByName}
                    onChange={handleOnboardedName}
                />
              </div>
              )}
              
              {/* Email */}
              <div className={styles.formGroup}>
                <label className={styles.required}>
                  {category === 'brand' ? 'Business Email' : 'Email'}
                </label>
                <input
                  type="email"
                  className={styles.inputField}
                  placeholder="Joe@shoprite.com"
                  value={email}
                  onChange={handleEmailChange}
                />
              </div>
              
              {/* Store/Business Name */}
              <div className={styles.formGroup}>
                <label className={styles.required}>
                  {category === 'brand' ? 'Business Name' : 'Store Name'}
                </label>
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder={category === 'brand' ? 'Your Business Name' : 'shoprite'}
                  value={storeName}
                  onChange={handleStoreNameChange}
                />
              </div>

              {/* Business/Store Address */}
              <div className={styles.formGroup}>
                <label className={styles.required}>
                  {category === 'brand' ? 'Business Address' : 'Store Address'}
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  {!currentLocationCheck && (
                    <p
                      style={{ 
                        cursor: 'pointer', 
                        fontSize: '15px', 
                        color: '#AF3585',
                        margin: '8px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onClick={handleUseLiveLocation}
                    >
                      <FmdGoodIcon /> Use My Current Location
                      {isLoading && (
                        <CircularProgress
                          style={{ color: '#AF3585' }}
                          size="0.8rem"
                        />
                      )}
                    </p>
                  )}
                  {currentLocationCheck && (
                    <p
                      style={{ 
                        cursor: 'pointer', 
                        fontSize: '15px', 
                        color: '#AF3585',
                        margin: '8px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onClick={handleManualLocation}
                    >
                      <FmdGoodIcon /> Manual Location (Edit)
                    </p>
                  )}
                </div>

                {currentLocationCheck && (
                  <div style={{ marginTop: '8px' }}>
                    <select
                      className={styles.selectField}
                      value={store_address}
                      onChange={handleAddressChange}
                      required
                    >
                      {addressResults.map((address, index) => (
                        <option key={index} value={address}>
                          {address}
                        </option>
                      ))}
                </select>
              </div>
                )}
                
                {!currentLocationCheck && (
                  <PlacesAutocomplete
                    value={store_address}
                    onChange={handleStoreAddressChange}
                    onSelect={handleSelect}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading,
                    }) => (
                      <div>
                        <input
                          {...getInputProps({
                            placeholder: 'Search Places ...',
                            className: styles.inputField,
                          })}
                        />
                        <div style={{ 
                          position: 'absolute', 
                          top: '100%', 
                          left: 0, 
                          right: 0, 
                          zIndex: 1000,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {loading && <div style={{ padding: '8px', textAlign: 'center' }}>Loading...</div>}
                          {suggestions?.map((suggestion, i) => (
                            <div
                              key={i}
                              {...getSuggestionItemProps(suggestion)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: suggestion.active ? '#f5f5f5' : 'white'
                              }}
                            >
                              {suggestion.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </PlacesAutocomplete>
                )}
              </div>

              {/* Business/Store Phone */}
              <div className={styles.formGroup}>
                <label className={styles.required}>
                  {category === 'brand' ? 'Business Phone' : 'Store Phone'}
                </label>
                <input
                  type="tel"
                  className={styles.inputField}
                  placeholder="+234 xxx xxx xxxx"
                  value={storePhone}
                  onChange={handleStorePhoneChange}
                />
              </div>
              
              {/* Product Categories - only show when category is 'brand' */}
              {category === 'brand' && (
                <div className={styles.formGroup}>
                  <label className={styles.required}>Product Categories*</label>
                  
                  {/* Selected Categories Input Field */}
                  <div style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '8px', 
                    padding: '12px',
                    backgroundColor: '#fff',
                    minHeight: '50px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    alignItems: 'center',
                    position: 'relative'
                  }}>
                    {selectedProductCategories.length > 0 ? (
                      selectedProductCategories.map((cat) => (
                        <div
                          key={cat.value}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '16px',
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            border: '1px solid #E5E7EB',
                            fontSize: '13px',
                            fontWeight: '400',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {cat.label}
                          <span
                            onClick={() => {
                              handleProductCategoryChange(
                                selectedProductCategories.filter(selected => selected.value !== cat.value)
                              );
                            }}
                            style={{
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              color: '#6B7280',
                              lineHeight: '1'
                            }}
                          >
                            ×
                          </span>
                        </div>
                      ))
                    ) : (
                      <span style={{ color: '#9CA3AF', fontSize: '14px' }}>
                        Select categories...
                      </span>
                    )}
                    
                    {/* Dropdown Arrow */}
                    <div style={{ 
                      position: 'absolute', 
                      right: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      cursor: 'pointer'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>

                  {/* Available Category Options */}
                  <div style={{ marginTop: '12px' }}>
                    <small style={{ color: '#666', display: 'block', marginBottom: '8px' }}>
                      Select all kind of product this business sells
                    </small>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {isLoadingCategories ? (
                        <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                          Loading categories...
                        </div>
                      ) : (
                        productCategories.map((cat) => {
                          const isSelected = selectedProductCategories.some(selected => selected.value === cat._id);
                          return (
                            <div
                              key={cat._id}
                              onClick={() => {
                                if (isSelected) {
                                  handleProductCategoryChange(
                                    selectedProductCategories.filter(selected => selected.value !== cat._id)
                                  );
                                } else {
                                  handleProductCategoryChange([
                                    ...selectedProductCategories,
                                    { label: cat.category_name, value: cat._id }
                                  ]);
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '16px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '400',
                                transition: 'all 0.2s ease',
                                backgroundColor: isSelected ? '#FCE7F3' : '#FFFFFF',
                                color: isSelected ? '#374151' : '#374151',
                                border: isSelected ? '1px solid #F472B6' : '1px solid #E5E7EB',
                                userSelect: 'none'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.target.style.backgroundColor = '#F9FAFB';
                                  e.target.style.borderColor = '#D1D5DB';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.target.style.backgroundColor = '#FFFFFF';
                                  e.target.style.borderColor = '#E5E7EB';
                                }
                              }}
                            >
                              {cat.category_name}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Country */}
                <div className={styles.formGroup}>
                  <label className={styles.required}>Country</label>
                <select
                  className={styles.selectField}
                  value={country}
                  onChange={handleCountryChange}
                >
                  <option value="" disabled>Select Country</option>
                  {hardcodedCountries.map((c, i) => (
                    <option key={i} value={c.name}>{c.name}</option>
                  ))}
                  </select>
                </div>
                
              {/* State */}
                <div className={styles.formGroup}>
                  <label className={styles.required}>State</label>
                <select
                  className={styles.selectField}
                  value={state}
                  onChange={handleStateChange}
                  disabled={!country}
                >
                  <option value="" disabled>Select State</option>
                  {states.map((s, i) => (
                    <option key={i} value={s}>{s}</option>
                  ))}
                  </select>
              </div>
              


              {/* Password */}
              <div className={styles.formGroup}>
                <label className={styles.required}>Password</label>
                <div style={{ position: 'relative' }}>
                <input
                    type={showText ? 'text' : 'password'}
                  className={styles.inputField}
                    placeholder="Create a password"
                    value={password}
                    onChange={handlePasswordChange}
                  />
                  <div 
                    style={{ 
                      position: 'absolute', 
                      right: '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      cursor: 'pointer'
                    }} 
                    onClick={changeType}
                  >
                    {!showText ? '👁️' : '🙈'}
                  </div>
                </div>
                <small>Enter at least 8 characters</small>
              </div>
              
              {/* Confirm Password */}
              <div className={styles.formGroup}>
                <label className={styles.required}>Confirm password</label>
                <div style={{ position: 'relative' }}>
                <input
                    type={showText1 ? 'text' : 'password'}
                  className={styles.inputField}
                    placeholder="Confirm password"
                    value={confirm_password}
                    onChange={handleCPasswordChange}
                  />
                  <div 
                    style={{ 
                      position: 'absolute', 
                      right: '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      cursor: 'pointer'
                    }} 
                    onClick={changeType1}
                  >
                    {!showText1 ? '👁️' : '🙈'}
                  </div>
                </div>
                {doesNotMath && (
                  <small style={{ color: 'red' }}>
                    Password confirmation does not match
                  </small>
                )}
              </div>

              {/* Terms and Conditions */}
              <div className={styles.formGroup}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={handleAgreeChange}
                    style={{ margin: 0 }}
                  />
                  I agree with the
                  <span style={{ color: 'royalblue', cursor: 'pointer', fontWeight: 'bold' }}>
                    terms of the service and conditions
                  </span>
                </label>
              </div>
              
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={handleNext}
                  className={styles.nextButton}
                  disabled={!agree || !category || !email || !storeName || !storeAddress || !storePhone || !country || !state || !password || !confirm_password || doesNotMath}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <h2>Create your virtual wallet</h2>
              <p>Please enter the relevant details.</p>
            </div>

            <div className={styles.form}>
              {/* Business Type */}
              <div className={styles.formGroup}>
                <label>Business Type</label>
                <select
                  className={styles.selectField}
                  value={formData.businessType}
                  onChange={(e) => updateFormData('businessType', e.target.value)}
                >
                  {businessTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Enter BVN */}
              <div className={styles.formGroup}>
                <label className={styles.required}>Enter BVN</label>
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder="Enter your 11-digit BVN"
                  value={formData.bvn}
                  onChange={(e) => updateFormData('bvn', e.target.value)}
                  maxLength={11}
                />
              </div>

              {/* Date of Birth */}
              <div className={styles.formGroup}>
                <label className={styles.required}>Date of Birth</label>
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder="dd/mm/yyyy"
                  value={formData.dateOfBirth}
                  onChange={(e) => updateFormData('dateOfBirth', e.target.value)}
                  maxLength={10}
                />
              </div>

              {/* ID Type */}
              <div className={styles.formGroup}>
                <label className={styles.required}>ID Type</label>
                <select
                  className={styles.selectField}
                  value={formData.idType}
                  onChange={(e) => updateFormData('idType', e.target.value)}
                >
                  {idTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* ID Number */}
              <div className={styles.formGroup}>
                <label className={styles.required}>ID Number</label>
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder="Enter ID number"
                  value={formData.idNumber}
                  onChange={(e) => updateFormData('idNumber', e.target.value)}
                />
              </div>

              {/* CAC Registration Number */}
              <div className={styles.formGroup}>
                <label>CAC Registration Number</label>
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder="Enter CAC registration number"
                  value={formData.cacRegistrationNumber}
                  onChange={(e) => updateFormData('cacRegistrationNumber', e.target.value)}
                />
              </div>

              {/* TIN */}
              <div className={styles.formGroup}>
                <label>TIN (Tax Identification Number)</label>
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder="Enter TIN"
                  value={formData.tin}
                  onChange={(e) => updateFormData('tin', e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className={styles.skipButton}
                  disabled={currentStep === 1}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className={styles.nextButton}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <h2>Upload Documents</h2>
              <p>Please enter the relevant details.</p>
            </div>
            <div className={styles.form}>
              {/* CAC Certificate Section */}
              <div className={styles.uploadSection}>
                {cacDocument ? (
                  <div className={styles.uploadHeader}>
                    <div className={styles.uploadIcon}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.6667 5L7.5 14.1667L3.33333 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className={styles.uploadText}>
                      <span className={styles.uploadStatus}>Uploading Document...</span>
                      <span className={styles.documentName}>{cacDocument.name}</span>
                    </div>
                    <div className={styles.clearUpload} onClick={() => handleClearUpload('cac')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Clear Upload</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.uploadArea} onClick={() => document.getElementById('cac-upload').click()}>
                      <div className={styles.cloudIcon}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 10H16.74C16.4379 7.54425 14.9441 5.33505 12.8321 4.13561C10.7201 2.93617 8.18358 2.88418 6.03275 3.98875C3.88192 5.09332 2.27206 7.24312 2.04258 9.7373C1.8131 12.2315 2.99475 14.7003 5.39999 16.3C5.39999 16.3 5.39999 16.3 5.39999 16.3" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 16L16 12L12 8" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 12H8" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className={styles.uploadInstructions}>
                        <span className={styles.uploadAction}>Click to upload or drag and drop</span>
                        <span className={styles.fileTypes}>SVG, PNG, JPG or GIF (max. 800x400px)</span>
                      </div>
                      <input
                        id="cac-upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'cac')}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className={styles.clearUploadSection}>
                      <button className={styles.clearUploadButton} onClick={() => handleClearUpload('cac')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6H5H21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Clear Upload</span>
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Means of ID Document Section */}
              <div className={styles.uploadSection}>
                {idDocument ? (
                  <div className={styles.uploadHeader}>
                    <div className={styles.uploadIcon}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.6667 5L7.5 14.1667L3.33333 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className={styles.uploadText}>
                      <span className={styles.uploadStatus}>Uploading Document...</span>
                      <span className={styles.documentName}>{idDocument.name}</span>
                    </div>
                    <div className={styles.clearUpload} onClick={() => handleClearUpload('id')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Clear Upload</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.uploadArea} onClick={() => document.getElementById('id-upload').click()}>
                      <div className={styles.cloudIcon}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 10H16.74C16.4379 7.54425 14.9441 5.33505 12.8321 4.13561C10.7201 2.93617 8.18358 2.88418 6.03275 3.98875C3.88192 5.09332 2.27206 7.24312 2.04258 9.7373C1.8131 12.2315 2.99475 14.7003 5.39999 16.3C5.39999 16.3 5.39999 16.3 5.39999 16.3" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 16L16 12L12 8" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 12H8" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className={styles.uploadInstructions}>
                        <span className={styles.uploadAction}>Click to upload or drag and drop</span>
                        <span className={styles.fileTypes}>SVG, PNG, JPG or GIF (max. 800x400px)</span>
                      </div>
                      <input
                        id="id-upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'id')}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className={styles.clearUploadSection}>
                      <button className={styles.clearUploadButton} onClick={() => handleClearUpload('id')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6H5H21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Clear Upload</span>
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Utility Bill Section */}
              <div className={styles.uploadSection}>
                {utilityBill ? (
                  <div className={styles.uploadHeader}>
                    <div className={styles.uploadIcon}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.6667 5L7.5 14.1667L3.33333 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className={styles.uploadText}>
                      <span className={styles.uploadStatus}>Uploading Document...</span>
                      <span className={styles.documentName}>{utilityBill.name}</span>
                    </div>
                    <div className={styles.clearUpload} onClick={() => handleClearUpload('utility')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Clear Upload</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.uploadArea} onClick={() => document.getElementById('utility-upload').click()}>
                      <div className={styles.cloudIcon}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 10H16.74C16.4379 7.54425 14.9441 5.33505 12.8321 4.13561C10.7201 2.93617 8.18358 2.88418 6.03275 3.98875C3.88192 5.09332 2.27206 7.24312 2.04258 9.7373C1.8131 12.2315 2.99475 14.7003 5.39999 16.3C5.39999 16.3 5.39999 16.3 5.39999 16.3" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 16L16 12L12 8" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 12H8" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className={styles.uploadInstructions}>
                        <span className={styles.uploadAction}>Click to upload or drag and drop</span>
                        <span className={styles.fileTypes}>SVG, PNG, JPG or GIF (max. 800x400px)</span>
                      </div>
                      <input
                        id="utility-upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'utility')}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className={styles.clearUploadSection}>
                      <button className={styles.clearUploadButton} onClick={() => handleClearUpload('utility')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6H5H21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Clear Upload</span>
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Verify your face Section */}
              <div className={styles.faceVerificationSection}>
                <div className={styles.faceVerificationContent}>
                  <div className={styles.faceIcon}>
                    <img 
                      src="/assets/images/fixial-kyc.png" 
                      alt="Face Verification" 
                      width="32" 
                      height="32"
                    />
                  </div>
                  <div className={styles.faceVerificationText}>
                    <span className={styles.faceVerificationTitle}>Verify your face</span>
                    <span className={styles.faceVerificationSubtitle}>Proceed to authenticate facial</span>
                  </div>
                  <button 
                    className={styles.verifyFacialButton}
                    onClick={handleFaceVerification}
                    disabled={faceVerified}
                  >
                    {faceVerified ? 'Verified ✓' : 'Verify Facial'}
                  </button>
                </div>
              </div>
              
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className={styles.skipButton}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className={styles.nextButton}
                >
                  Complete KYC
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`row ${styles.sav}`}>
      <div className="col-md-6 d-none d-md-block">
        <div className={styles.bg}>
          <div className={styles.content}>
            <h5>
              <div key={welcomeTextIndex} className={styles.welcomeText}>
                {welcomeMessages[welcomeTextIndex].title}<br />
                {welcomeMessages[welcomeTextIndex].subtitle}
              </div>
            </h5>
            
            <div className={styles.progressCard}>
              <h6>Complete KYC</h6>
              <p>
                Context about the tasks in the checklist below users
                should prioritize completing as they get started.
              </p>
              
              <div className={styles.progressBar}>
                <span>{currentStep}/3</span>
                <div className={styles.progressTrack}>
                  <div 
                    className={styles.progressFill}
                    style={{ width: `${(currentStep / 3) * 100}%` }}
                  />
                </div>
              </div>

              <div className={styles.stepsList}>
                <div className={`${styles.step} ${currentStep === 1 ? styles.active : ''} ${currentStep > 1 ? styles.completed : ''}`}>
                  <span className={styles.stepIcon}>
                    {currentStep > 1 ? '✓' : '1'}
                  </span>
                  Business Information
                </div>
                
                <div className={`${styles.step} ${currentStep === 2 ? styles.active : ''} ${currentStep > 2 ? styles.completed : ''}`}>
                  <span className={styles.stepIcon}>
                    {currentStep > 2 ? '✓' : '2'}
                  </span>
                  Create Wallet
                </div>
                
                <div className={`${styles.step} ${currentStep === 3 ? styles.active : ''}`}>
                  <span className={styles.stepIcon}>3</span>
                  Upload Documents
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className={`${styles.sign_up} container`}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
